import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import makeStyles from '@mui/styles/makeStyles';
import { Card, CardContent, Link, Typography, List, ListItem, FormHelperText } from '@mui/material';
import combineStyles from 'classnames';
import { fetchKioskSurveys, enableKioskMode, saveCompanyKioskId, fetchCompany, setLanguageISO } from '../redux/actions';
import {
  WORKTANGO_URL,
  TERMS_OF_USE_URL,
  PRIVACY_POLICY_URL,
  PRIVACY_NOTICE_URL,
  createSurveyUriWithQueryParams,
} from '../helpers/link_helper';
import KioskLogin from '../components/KioskLogin';
import KioskList from '../components/KioskList';
import Loading from '../components/Loading';
import { useTranslation } from 'react-i18next';
import { KIOSK_ID } from '../constants/kiosk_company_code_types';
import LoadingPage from './LoadingPage';
import NoMatchPage from './NoMatchPage';
import WorktangoLogo from '../assets/worktango-logo.svg?react'; // DEPRECATED: no longer needed once rebrand is enabled
import WorktangoWordmark from '../assets/worktango_wordmark.svg?react';
import { useAppDispatch, useAppSelector } from '../redux/ReduxProvider';

const ENABLE_REBRAND = import.meta.env.VITE_ENABLE_REBRAND === 'true';
const MAX_LOGO_HEIGHT = 80;
const MAX_LOGO_WIDTH = 360;

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100%',
    overflow: 'auto',
    backgroundColor: theme.palette.darkaccent,
  },
  skipLink: {
    position: 'absolute',
    top: 8,
    left: 8,
    textDecoration: 'none',
    color: 'transparent',
    width: 0,
    height: 0,
  },
  visibleSkipLink: {
    width: 'unset',
    height: 'unset',
    color: '#ffffff',
  },
  cardWrapper: {
    display: 'flex',
    flexDirection: 'column',
    margin: 'auto',
  },
  card: {
    width: 480,
    backgroundColor: theme.palette.common.white,
    marginTop: 12,
    paddingBottom: 24,
    [theme.breakpoints.down(500)]: {
      width: 'auto',
      maxWidth: 480,
    },
  },
  cardFooter: {
    color: theme.palette.darkaccentLight,
    backgroundColor: 'transparent',
    textAlign: 'center',
    marginTop: 48,
    fontSize: '.75rem',
  },
  cardFooterList: {
    display: 'flex',
    flexDirection: 'row',
    padding: 0,
    justifyContent: 'center',
    width: '100%',
    '& li': {
      padding: '0 16px',
      width: 'unset',
      justifyContent: 'center',
      color: '#ffffff',
      fontSize: 12,
      fontWeight: 500,
    },
  },
  poweredBy: {
    display: 'flex',
    alignContent: 'center',
    justifyContent: 'center',
    marginTop: 48,
  },
  poweredText: {
    color: '#4f4f4f',
    fontSize: 14,
    fontWeight: 400,
    marginRight: 8,
  },
  poweredLogo: {
    // DEPRECATED: no longer needed once rebrand is enabled
    width: 68,
  },
  worktangoLogo: {
    width: 'auto',
    height: 21,
  },
  footer: {
    margin: 16,
    color: '#f2f2f2',
    fontWeight: 400,
    fontSize: 12,
  },
  worktangoFooter: {
    marginRight: '.25rem',
    marginLeft: '.25rem',
  },
  companyLogoContainer: {
    display: 'flex',
    justifyContent: 'center',
    margin: '24px 24px 8px 24px',
  },
  companyLogo: {
    maxHeight: MAX_LOGO_HEIGHT,
    maxWidth: MAX_LOGO_WIDTH,
  },
  errorText: {
    paddingTop: theme.spacing(2),
  },
  loader: {
    marginTop: 0,
  },
}));

const toTitleCase = (str) => {
  return str
    .toLowerCase()
    .split(' ')
    .map((word) => {
      return word.replace(word[0], word[0].toUpperCase());
    })
    .join(' ');
};

const humanFriendlyList = (labels, connector) => {
  if (!labels || !labels.length) {
    return '';
  }

  const titleizedLabels = labels.map(toTitleCase);
  const lastLabels = titleizedLabels.splice(-2, 2);

  return [...titleizedLabels, lastLabels.join(` ${connector} `)].join(', ');
};

const KioskPage = () => {
  const styles = useStyles();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { company_kiosk_id: companyKioskId } = useParams();
  const createSurveyUri = createSurveyUriWithQueryParams(location.search);

  const [isSkipLinkVisible, setIsSkipLinkVisible] = useState(false);

  const {
    id: companyId,
    logoUrl,
    employeeIdTypes,
    kioskWelcomeText,
    isLoading: isLoadingCompany,
    hasFetchedData,
  } = useAppSelector((state) => state.company);
  const { surveyList, error, isLoading: isLoadingKiosk } = useAppSelector((state) => state.kiosk);

  const fieldLabel = humanFriendlyList(employeeIdTypes, t('or'));

  useEffect(() => {
    dispatch(fetchCompany(companyKioskId, KIOSK_ID));
    // eslint-disable-next-line
  }, [companyKioskId]);

  const gotoSurvey = (hashCode) => {
    dispatch(enableKioskMode());
    navigate(createSurveyUri(hashCode));
  };

  const selectSurvey = (survey) => {
    dispatch(enableKioskMode());
    dispatch(setLanguageISO(survey.langISOCode));
  };

  const activeSkipLink = () => {
    setIsSkipLinkVisible(true);
  };

  const deactiveSkipLink = () => {
    setIsSkipLinkVisible(false);
  };

  const renderKioskContent = () => {
    if (surveyList && surveyList.length > 0) {
      return <KioskList linkToSurvey={createSurveyUri} surveyList={surveyList} onClickSurvey={selectSurvey} />;
    }

    const onSubmit = (id) => {
      dispatch(fetchKioskSurveys(gotoSurvey, companyId, id));
      dispatch(saveCompanyKioskId(companyKioskId));
    };

    return (
      <KioskLogin
        label={fieldLabel}
        idTypes={employeeIdTypes}
        welcomeText={kioskWelcomeText}
        onSubmit={onSubmit}
        errorMsg={error}
      />
    );
  };

  if (!hasFetchedData || isLoadingCompany) {
    return <LoadingPage />;
  }

  if (hasFetchedData && companyId === null) {
    return <NoMatchPage />;
  }

  return (
    <div className={styles.root}>
      <a
        className={combineStyles(styles.skipLink, {
          [styles.visibleSkipLink]: isSkipLinkVisible,
        })}
        href="#main"
        onFocus={activeSkipLink}
        onBlur={deactiveSkipLink}
      >
        {t('accessibility.skipToContent')}
      </a>
      <section className={styles.cardWrapper}>
        <Card className={styles.card}>
          <div className={styles.companyLogoContainer} role="banner">
            <img src={logoUrl} alt="company logo" className={styles.companyLogo} />
          </div>

          <CardContent id="main" role="main">
            {error && (
              <FormHelperText error id="error-text" className={styles.errorText} role="alert" aria-live="Assertive">
                {error}
              </FormHelperText>
            )}

            {renderKioskContent()}

            <div className={styles.poweredBy}>
              <Typography className={styles.poweredText}>{t('survey.kiosk.poweredby')}</Typography>
              <Link href={WORKTANGO_URL} underline="hover">
                {!ENABLE_REBRAND && <WorktangoLogo className={styles.poweredLogo} alt="WorkTango" />}
                {ENABLE_REBRAND && <WorktangoWordmark className={styles.worktangoLogo} alt="WorkTango" />}
              </Link>
            </div>
          </CardContent>

          <Loading className={styles.loader} isVisible={isLoadingKiosk} />
        </Card>
        <Typography className={styles.cardFooter} component="footer" role="contentinfo">
          <List className={styles.cardFooterList}>
            <ListItem>
              <Link href={TERMS_OF_USE_URL} color="inherit" underline="hover">
                {t('user.terms')}
              </Link>
            </ListItem>
            <ListItem>
              <Link href={PRIVACY_POLICY_URL} color="inherit" underline="hover">
                {t('user.policy')}
              </Link>
            </ListItem>
            <ListItem>
              <Link href={PRIVACY_NOTICE_URL} color="inherit" underline="hover">
                {t('user.notice')}
              </Link>
            </ListItem>
          </List>
          <p className={styles.footer}>{t('kiosk.footer', { year: new Date().getFullYear() })}</p>
        </Typography>
      </section>
    </div>
  );
};

export default KioskPage;
